import {FC, useCallback, useEffect, useRef, useState} from "react";
import FortuneCookie from "./FortuneCookie.tsx";
import { useWidgetData } from "../../hooks/useWidgetData.ts";
import { getSSRTranslation } from '../../../i18n.ts';
import { FeedQueryResult } from "../../net/softbox-types.ts";
import "../WidgetCard/WidgetCard.scss";
import { useTranslation } from 'react-i18next';

const FortuneCookieWrapper: FC = () => {
  const { i18n, t } = useTranslation();
  const [currentFortune, setCurrentFortune] = useState<string>('');
  const unseenFortunesRef = useRef<string[]>([]);

  // This is our "class method" - the data processor specific to fortunes
  const processFortuneData = useCallback((feedData: FeedQueryResult): string[] => {
    if (!feedData?.news?.items) {
      return [];
    }

    // Extract fortune strings from the items
    return feedData.news.items.map((item: any) => {
      // Try to get fortune from title first, then summary
      const fortuneText = getSSRTranslation(item.title, i18n) || getSSRTranslation(item.summary, i18n);
      return fortuneText;
    }).filter((fortune: string) => {return fortune && fortune.trim().length > 0});
  }, []);

  // Use our generic hook - this is the "inherited" functionality
  const { data: fortunes, loading, error, refetch } = useWidgetData<string>('fortune-cookie', processFortuneData);

  const updateFortune = useCallback(() => {
    // If unseenFortunesRef is empty, reset it from the original fortunes
    if (unseenFortunesRef.current.length === 0) {
      unseenFortunesRef.current = [...fortunes];
    }

    const newFortune = unseenFortunesRef.current.pop() ?? '';
    setCurrentFortune(newFortune);
  }, [fortunes]);

  // Set up fortunes when they change
  useEffect(() => {
    if (fortunes.length > 0) {
      unseenFortunesRef.current = [...fortunes];
      updateFortune(); // Set initial fortune
    }
  }, [fortunes, updateFortune]);

  return (
    <div className="widget-card" data-testid="test-widget-card">
      <h2 className="widget-card__title header-md">{t("Fortune Cookie")}</h2>
      <div className="widget-card__content">
        {/* Show loading state */}
        {loading && <FortuneCookie getNewFortune={() => {}} fortune="Loading fortunes..." />}

        {/* Show error state */}
        {error && <FortuneCookie getNewFortune={() => {return refetch()}} fortune={`Error: ${error}. Tap to retry.`} />}

        {/* Show fortune content */}
        {!loading && !error && fortunes.length > 0 && (
          <FortuneCookie getNewFortune={updateFortune} fortune={currentFortune} />
        )}

        {/* No fortunes available */}
        {!loading && !error && fortunes.length === 0 && (
          <FortuneCookie getNewFortune={() => {return refetch()}} fortune="No fortunes available. Tap to retry." />
        )}
      </div>
    </div>
  );
};

export default FortuneCookieWrapper;
